import { isEmpty, isNil, last } from 'lodash-es'
import type { RouteSegment, StreetSegmentPlanningData } from '@/api/main/types.gen'
import { applyGapFixesToSegments, type GapFix } from './gapFix'
import { mergeRemovalWithVisit } from './removalRange'
import { findTailReconnectIndex } from './routePlanUtils'
import { canUndoLastSegment, hasUnsavedRouteChanges } from './unsavedChangesGate'
import type { ActivePicker, EditState, InteractionMode, PathState, PendingRemoval } from './types'

/**
 * Pure planner core: one reducer owns path, edit session, operation history, mode,
 * and transient interaction state. Invariants enforced here rather than at call sites:
 *
 * - `CANCEL_EDIT` restores the exact pre-edit path (kept in `edit.preEditPath`).
 * - `DELETE_LAST` never eats the preserved head below `edit.editFromSegmentIndex`.
 * - Operation actions push one history snapshot except `ACCEPT_HINT` during an
 *   active edit (relies on the `START_EDIT` snapshot). Plain appends push none.
 * - Path-mutating actions clear transient targets (add target, pending removal, picker).
 * - `lastNode` always tracks the final segment exit after splices.
 *
 * Actions that can be invalid (`COMMIT_EDIT`, `ACCEPT_TAIL`) are no-ops in the reducer;
 * callers consult `selectCanCommitEdit` / `selectCanAcceptTail` first and surface the
 * `reconnectRequired` notification themselves. The reducer never performs queries,
 * navigation, or graph searches — shortest-path results arrive via action payloads.
 */

export type PlannerEditState = EditState & {
  preEditPath: PathState
  /** `history.length` before the `START_EDIT` snapshot was pushed. */
  historyLengthAtStart: number
}

export type PlannerCoreState = {
  mode: InteractionMode
  path: PathState
  edit: PlannerEditState | null
  /** Loaded baseline used for dirty checks and reset (was `originalLoadedSegments`). */
  original: RouteSegment[] | null
  loadedRouteKey: string | null
  /**
   * Monotonic counter distinguishing repeated fit requests for the same route
   * (see `selectRouteFitKey`). Never reset, so composite fit keys cannot repeat.
   */
  fitNonce: number
  /** Operation snapshots, edit-flattened (head + detached tail). */
  history: PathState[]
  addTarget: string | null
  pendingRemoval: PendingRemoval | null
  gapFixPreview: boolean
  activePicker: ActivePicker | null
}

export type PlannerAction =
  | { type: 'APPEND'; nodeId: string; segment?: StreetSegmentPlanningData }
  | { type: 'DELETE_LAST' }
  | { type: 'START_EDIT'; segmentIndex: number }
  | { type: 'CANCEL_EDIT' }
  | { type: 'COMMIT_EDIT' }
  | { type: 'ACCEPT_TAIL' }
  | { type: 'ACCEPT_HINT'; hint: RouteSegment[] }
  | { type: 'MERGE_REMOVAL_VISIT'; visitIndex: number }
  | { type: 'SET_PENDING_REMOVAL'; pendingRemoval: PendingRemoval | null }
  | { type: 'CONFIRM_REMOVAL'; loopStart: number; loopEnd: number; replacement: RouteSegment[] }
  | { type: 'APPLY_GAP_FIXES'; fixes: GapFix[] }
  | { type: 'UNDO_OPERATION' }
  | { type: 'RESET_HISTORY' }
  | { type: 'LOAD_PLAN'; segments: RouteSegment[]; routeKey: string | null }
  | { type: 'ADOPT_SAVED'; segments: RouteSegment[]; routeKey: string | null }
  | { type: 'RESET_TO_ORIGINAL' }
  | { type: 'CLEAR_PLAN' }
  | { type: 'INVALIDATE_LOADED_ROUTE' }
  | { type: 'REQUEST_ROUTE_REFIT' }
  | { type: 'SET_MODE'; mode: InteractionMode }
  | { type: 'SET_ADD_TARGET'; nodeId: string | null }
  | { type: 'SET_ACTIVE_PICKER'; picker: ActivePicker | null }
  | { type: 'SET_GAP_FIX_PREVIEW'; preview: boolean }
  | { type: 'CLEAR_TRANSIENTS' }

const EMPTY_PATH: PathState = { segments: [] }

export function initialPlannerState(mode: InteractionMode): PlannerCoreState {
  return {
    mode,
    path: EMPTY_PATH,
    edit: null,
    original: null,
    loadedRouteKey: null,
    fitNonce: 0,
    history: [],
    addTarget: null,
    pendingRemoval: null,
    gapFixPreview: false,
    activePicker: null,
  }
}

function pathFromSegments(segments: RouteSegment[]): PathState {
  return { segments, lastNode: last(segments)?.exit_node_id }
}

/** Head + detached tail: what the plan will contain once the edit resolves. */
function flattenedPath(path: PathState, edit: PlannerEditState | null): PathState {
  if (!edit || isEmpty(edit.originalTail)) return path
  return {
    segments: [...path.segments, ...edit.originalTail],
    lastNode: last(edit.originalTail)!.exit_node_id,
  }
}

const CLEARED_TRANSIENTS = {
  addTarget: null,
  pendingRemoval: null,
  activePicker: null,
} as const

function withSnapshot(state: PlannerCoreState): PathState[] {
  return [...state.history, flattenedPath(state.path, state.edit)]
}

export function plannerReducer(state: PlannerCoreState, action: PlannerAction): PlannerCoreState {
  switch (action.type) {
    case 'APPEND': {
      const { nodeId, segment } = action
      const prev = state.path
      if (prev.lastNode === nodeId) return state
      if (prev.lastNode) {
        if (!segment) return state
        const reverseDrive = segment.source_node_id === nodeId
        const appended: RouteSegment = {
          street_segment_id: segment.street_segment_id,
          entrance_node_id: !reverseDrive ? segment.source_node_id : segment.destination_node_id,
          exit_node_id: !reverseDrive ? segment.destination_node_id : segment.source_node_id,
        }
        return {
          ...state,
          ...CLEARED_TRANSIENTS,
          path: { segments: [...prev.segments, appended], lastNode: nodeId },
        }
      }
      return { ...state, ...CLEARED_TRANSIENTS, path: { ...prev, lastNode: nodeId } }
    }

    case 'DELETE_LAST': {
      if (isEmpty(state.path.segments)) return state
      if (state.edit && state.path.segments.length <= state.edit.editFromSegmentIndex) return state
      const segments = state.path.segments.slice(0, -1)
      return {
        ...state,
        ...CLEARED_TRANSIENTS,
        path: pathFromSegments(segments),
      }
    }

    case 'START_EDIT': {
      if (state.edit) return state
      const tail = state.path.segments.slice(action.segmentIndex)
      if (isEmpty(tail)) return state
      return {
        ...state,
        ...CLEARED_TRANSIENTS,
        history: withSnapshot(state),
        edit: {
          editFromSegmentIndex: action.segmentIndex,
          originalTail: tail,
          preEditPath: state.path,
          historyLengthAtStart: state.history.length,
        },
        path: {
          segments: state.path.segments.slice(0, action.segmentIndex),
          lastNode: tail[0]!.entrance_node_id,
        },
      }
    }

    case 'CANCEL_EDIT': {
      if (!state.edit) return state
      return {
        ...state,
        ...CLEARED_TRANSIENTS,
        path: state.edit.preEditPath,
        history: state.history.slice(0, state.edit.historyLengthAtStart),
        edit: null,
      }
    }

    case 'COMMIT_EDIT': {
      if (!state.edit) return state
      const tail = state.edit.originalTail
      if (isEmpty(tail)) return { ...state, ...CLEARED_TRANSIENTS, edit: null }
      const idx = findTailReconnectIndex(tail, state.path.lastNode)
      if (isNil(idx)) return state
      const remainingTail = tail.slice(idx)
      const segments = [...state.path.segments, ...remainingTail]
      return {
        ...state,
        ...CLEARED_TRANSIENTS,
        path: {
          segments,
          lastNode: last(remainingTail)?.exit_node_id ?? state.path.lastNode,
        },
        edit: null,
      }
    }

    case 'ACCEPT_TAIL': {
      if (!state.edit) return state
      const tail = state.edit.originalTail
      const tailStartNode = tail[0]?.entrance_node_id
      if (tailStartNode !== undefined && state.path.lastNode !== tailStartNode) return state
      return {
        ...state,
        ...CLEARED_TRANSIENTS,
        path: {
          segments: [...state.path.segments, ...tail],
          lastNode: last(tail)?.exit_node_id ?? state.path.lastNode,
        },
        edit: null,
      }
    }

    case 'ACCEPT_HINT': {
      const { hint } = action
      if (isEmpty(hint)) return state
      const hintEnd = last(hint)!.exit_node_id
      if (!state.edit) {
        return {
          ...state,
          ...CLEARED_TRANSIENTS,
          history: withSnapshot(state),
          path: { segments: [...state.path.segments, ...hint], lastNode: hintEnd },
        }
      }
      const tail = state.edit.originalTail
      const reconnectIndex = findTailReconnectIndex(tail, hintEnd)
      if (isNil(reconnectIndex)) {
        return {
          ...state,
          ...CLEARED_TRANSIENTS,
          path: { segments: [...state.path.segments, ...hint], lastNode: hintEnd },
        }
      }
      const segments = [...state.path.segments, ...hint, ...tail.slice(reconnectIndex)]
      return {
        ...state,
        ...CLEARED_TRANSIENTS,
        path: { segments, lastNode: last(segments)?.exit_node_id ?? hintEnd },
        edit: null,
      }
    }

    case 'MERGE_REMOVAL_VISIT': {
      return {
        ...state,
        pendingRemoval: mergeRemovalWithVisit(state.pendingRemoval, selectPathNodes(state), action.visitIndex),
      }
    }

    case 'SET_PENDING_REMOVAL':
      if (state.pendingRemoval === action.pendingRemoval) return state
      return { ...state, pendingRemoval: action.pendingRemoval }

    case 'CONFIRM_REMOVAL': {
      const { loopStart, loopEnd, replacement } = action
      const segments = [
        ...state.path.segments.slice(0, loopStart),
        ...replacement,
        ...state.path.segments.slice(loopEnd),
      ]
      return {
        ...state,
        ...CLEARED_TRANSIENTS,
        history: withSnapshot(state),
        path: pathFromSegments(segments),
      }
    }

    case 'APPLY_GAP_FIXES': {
      if (isEmpty(action.fixes)) return state
      const segments = applyGapFixesToSegments(state.path.segments, action.fixes)
      return {
        ...state,
        ...CLEARED_TRANSIENTS,
        history: withSnapshot(state),
        path: { segments, lastNode: last(segments)?.exit_node_id ?? state.path.lastNode },
        gapFixPreview: false,
      }
    }

    case 'UNDO_OPERATION': {
      const snapshot = last(state.history)
      if (!snapshot) return state
      return {
        ...state,
        ...CLEARED_TRANSIENTS,
        history: state.history.slice(0, -1),
        path: snapshot,
        edit: null,
      }
    }

    case 'RESET_HISTORY':
      return { ...state, history: [] }

    case 'LOAD_PLAN':
      return {
        ...state,
        ...CLEARED_TRANSIENTS,
        path: pathFromSegments(action.segments),
        original: action.segments,
        loadedRouteKey: action.routeKey,
        fitNonce: state.fitNonce + 1,
        edit: null,
        history: [],
      }

    case 'ADOPT_SAVED':
      return {
        ...state,
        ...CLEARED_TRANSIENTS,
        path: pathFromSegments(action.segments),
        original: action.segments,
        loadedRouteKey: action.routeKey,
        fitNonce: state.fitNonce + 1,
        edit: null,
        history: [],
      }

    case 'RESET_TO_ORIGINAL': {
      if (!state.original) return state
      return {
        ...state,
        ...CLEARED_TRANSIENTS,
        path: pathFromSegments(state.original),
        edit: null,
        history: [],
      }
    }

    case 'CLEAR_PLAN':
      return {
        ...state,
        ...CLEARED_TRANSIENTS,
        path: EMPTY_PATH,
        original: null,
        loadedRouteKey: null,
        edit: null,
        history: [],
      }

    case 'INVALIDATE_LOADED_ROUTE':
      if (state.loadedRouteKey === null) return state
      return {
        ...state,
        ...CLEARED_TRANSIENTS,
        loadedRouteKey: null,
        edit: null,
        history: [],
      }

    case 'REQUEST_ROUTE_REFIT':
      if (state.loadedRouteKey === null) return state
      return { ...state, fitNonce: state.fitNonce + 1 }

    case 'SET_MODE': {
      if (action.mode === state.mode) return state
      const editCancelled = state.edit ? state.edit.preEditPath : state.path
      const cancelEditForMode = action.mode === 'delete' || action.mode === 'create'
      let history = state.history
      if (action.mode === 'create') {
        history = []
      } else if (cancelEditForMode && state.edit) {
        history = state.history.slice(0, state.edit.historyLengthAtStart)
      }
      return {
        ...state,
        ...CLEARED_TRANSIENTS,
        mode: action.mode,
        path: cancelEditForMode ? editCancelled : state.path,
        edit: cancelEditForMode ? null : state.edit,
        history,
      }
    }

    case 'SET_ADD_TARGET':
      if (state.addTarget === action.nodeId) return state
      return { ...state, addTarget: action.nodeId }

    case 'SET_ACTIVE_PICKER':
      if (state.activePicker === action.picker) return state
      return { ...state, activePicker: action.picker }

    case 'SET_GAP_FIX_PREVIEW':
      if (state.gapFixPreview === action.preview) return state
      return { ...state, gapFixPreview: action.preview }

    case 'CLEAR_TRANSIENTS':
      if (!state.addTarget && !state.pendingRemoval && !state.activePicker) return state
      return { ...state, ...CLEARED_TRANSIENTS }
  }
}

// Selectors

/** Entrance of every segment plus the final `lastNode`. */
export function selectPathNodes(state: PlannerCoreState): string[] {
  return [
    ...state.path.segments.map(segment => segment.entrance_node_id),
    ...(state.path.lastNode ? [state.path.lastNode] : []),
  ]
}

/** During an edit, the detached tail still counts toward the planned route. */
export function selectEffectivePath(state: PlannerCoreState): PathState {
  return flattenedPath(state.path, state.edit)
}

export function selectHasUnsavedChanges(state: PlannerCoreState): boolean {
  return hasUnsavedRouteChanges(state.original, state.path.segments)
}

export function selectCanUndoSegment(state: PlannerCoreState): boolean {
  return canUndoLastSegment(state.original, state.path.segments, state.edit?.editFromSegmentIndex)
}

export function selectCanUndoOperation(state: PlannerCoreState): boolean {
  return state.history.length > 0
}

export function selectCanCommitEdit(state: PlannerCoreState): boolean {
  if (!state.edit) return true
  const tail = state.edit.originalTail
  if (isEmpty(tail)) return true
  return !isNil(findTailReconnectIndex(tail, state.path.lastNode))
}

export function selectCanAcceptTail(state: PlannerCoreState): boolean {
  if (!state.edit) return true
  const tailStartNode = state.edit.originalTail[0]?.entrance_node_id
  return tailStartNode === undefined || state.path.lastNode === tailStartNode
}

/**
 * Camera-fit key for the map: changes when a different route loads or when a
 * refit of the same route is requested. Consumers compare it opaquely.
 */
export function selectRouteFitKey(state: PlannerCoreState): string | null {
  return state.loadedRouteKey === null ? null : `${state.loadedRouteKey}#${state.fitNonce}`
}
